import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSave, faArrowLeft, faList, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { createCaregiver, updateCaregiver, fetchCaregiver } from '../../../../store/slices/caregiverSlice';
import { fetchPractice, addCaregiverOptimistic } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import './AddCaregiver.css';
import api from '../../../../services/api';
import { validatePassword } from '../../../../utils/passwordValidation';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import { buildPersonFormRows } from '../../../../components/Common/AddFormModel/personFormRows';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import ReassignmentModal from '../../../../components/Common/ReassignmentModal/ReassignmentModal.jsx';

const AddCaregiver = () => {
  const navigate = useNavigate();
  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('editId');
  const dispatch = useDispatch();
  const { currentCaregiver, isLoading, isUpdating, isCreating } = useSelector(state => state.caregiver);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const isEditMode = !!editId;
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    email: '',
    twoWayAuth: 'Disabled',
    timeZone: '',
    status: 'Active'
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [alternativeCaregivers, setAlternativeCaregivers] = useState([]);
  const [reassignments, setReassignments] = useState({}); // { patientId: newCaregiverId }
  const [isReassigning, setIsReassigning] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [isCheckingPasswordReuse, setIsCheckingPasswordReuse] = useState(false);
  const passwordReuseCheckTimerRef = React.useRef(null);
  const passwordReuseRequestIdRef = React.useRef(0);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    return JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !==
      JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
  }, [formData, initialFormData, isEditMode]);

  const [errors, setErrors] = useState({});
  const hasPasswordReuseError = String(errors.password || '').toLowerCase().includes('different from current password');

  // Fetch caregiver data when in edit mode
  useEffect(() => {
    if (isEditMode && editId) {
      dispatch(fetchCaregiver({ practiceId, caregiverId: editId }));
    }
  }, [isEditMode, editId, practiceId, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    return () => {
      if (passwordReuseCheckTimerRef.current) {
        clearTimeout(passwordReuseCheckTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!practiceId) return;
    const practiceName = currentPractice?.practice_name || 'Practice';
    updateBreadcrumbs([
      { label: 'Practices', path: '/practices' },
      { label: practiceName, path: `/practices/${practiceId}?tab=practice` },
      { label: 'Caregivers', path: `/practices/${practiceId}?tab=caregiver` },
      { label: isEditMode ? 'Edit Caregiver' : 'Add Caregiver', path: null, active: true }
    ]);
  }, [practiceId, isEditMode, currentPractice, updateBreadcrumbs]);

  // Populate form when caregiver data is loaded
  useEffect(() => {
    if (isEditMode && currentCaregiver) {
      const stateId = currentCaregiver.address?.state_id || currentCaregiver.state_id;
      const state = US_STATES.find(s => s.id === stateId)?.name || '';

      const mappedData = {
        title: (currentCaregiver.title_id !== null && currentCaregiver.title_id !== undefined)
          ? String(currentCaregiver.title_id)
          : (TITLES.find(t => t.label === currentCaregiver.title)?.id ? String(TITLES.find(t => t.label === currentCaregiver.title).id) : ''),
        firstName: currentCaregiver.first_name || '',
        middleInitial: currentCaregiver.middle_name || '',
        lastName: currentCaregiver.last_name || '',
        username: currentCaregiver.username || currentCaregiver.user?.username || '',
        password: '',
        confirmPassword: '',
        addressLine1: currentCaregiver.address?.address_line_1 || currentCaregiver.address_line_1 || '',
        addressLine2: currentCaregiver.address?.address_line_2 || currentCaregiver.address_line_2 || '',
        city: currentCaregiver.address?.city || currentCaregiver.city || '',
        state: state,
        stateId: stateId || null,
        zipCode: currentCaregiver.address?.zip_code || currentCaregiver.zip_code ? String(currentCaregiver.address?.zip_code || currentCaregiver.zip_code) : '',
        homePhone: formatPhoneNumber(currentCaregiver.phone || ''),
        cellPhone: formatPhoneNumber(currentCaregiver.cell_phone || ''),
        fax: formatPhoneNumber(currentCaregiver.fax || ''),
        email: currentCaregiver.email || '',
        twoWayAuth: (currentCaregiver.is_two_way_auth === 1 || currentCaregiver.is_two_way_auth === true || currentCaregiver.two_way_auth === 'Enabled' || currentCaregiver.two_way_authentication === 'Enabled') ? 'Enabled' : 'Disabled',
        timeZone: normalizeTimeZone(currentCaregiver.time_zone) || '',
        status: currentCaregiver.status ? (currentCaregiver.status.charAt(0).toUpperCase() + currentCaregiver.status.slice(1).toLowerCase()) : 'Active'
      };
      setFormData(mappedData);
      setInitialFormData(mappedData);
    }
  }, [isEditMode, currentCaregiver]);

  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} - ${phoneNumber.slice(6, 10)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        const formattedValue = formatPhoneNumber(numericValue);
        setFormData(prev => ({ ...prev, [name]: formattedValue }));
        if (name === 'homePhone' && numericValue.length === 0) {
          newErrors.homePhone = 'Home Phone is required';
        } else if (numericValue.length > 0 && numericValue.length < 10 && name !== 'fax') {
          newErrors[name] = 'Invalid phone format';
        } else {
          delete newErrors[name];
        }
        setErrors(newErrors);
      }
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 2) {
          newErrors.zipCode = 'Zip code must be at least 2 characters';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return; 
      }
      if ((name === 'firstName' || name === 'lastName') && value.length > 20) {
        return;
      }
      if (name === 'middleInitial') {
        if (value.includes(' ')) {
          newErrors.middleInitial = 'Middle Initial cannot contain spaces';
        } else {
          delete newErrors.middleInitial;
        }
      }
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      if (passwordReuseCheckTimerRef.current) {
        clearTimeout(passwordReuseCheckTimerRef.current);
        passwordReuseCheckTimerRef.current = null;
      }
      passwordReuseRequestIdRef.current += 1;
      setIsCheckingPasswordReuse(false);

      // In Edit mode, if the value is empty, we don't change the password, so no error.
      // In Add mode, we don't show the "required" error in real-time until they click Add.
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        // Only show length/complexity errors if the user has typed something
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          // In Edit Mode, if it's just a length error and the user might be starting to type, 
          // or if it's an auto-fill, we might want to be careful.
          // But usually, real-time feedback is preferred. 
          // The user specifically wants it "not by default".
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });

          if (isEditMode && editId && value.trim()) {
            const requestId = passwordReuseRequestIdRef.current;
            setIsCheckingPasswordReuse(true);
            passwordReuseCheckTimerRef.current = setTimeout(async () => {
              try {
                await api.put(
                  `/practices/${practiceId}/caregivers/${editId}?validatePasswordOnly=1`,
                  { password: value }
                );
                if (requestId === passwordReuseRequestIdRef.current) {
                  setErrors(prev => {
                    const next = { ...prev };
                    if (String(next.password || '').toLowerCase().includes('different from current password')) {
                      delete next.password;
                    }
                    return next;
                  });
                }
              } catch (err) {
                const msg = err?.response?.data?.message || '';
                if (
                  requestId === passwordReuseRequestIdRef.current &&
                  String(msg).toLowerCase().includes('different from current password')
                ) {
                  setErrors(prev => ({ ...prev, password: msg }));
                }
              } finally {
                if (requestId === passwordReuseRequestIdRef.current) {
                  setIsCheckingPasswordReuse(false);
                }
              }
            }, 350);
          }
        }
      }
    }

    // 4. City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 5. Status Change Detection
    if (name === 'status' && isEditMode) {
      const currentStatus = formData.status;
      if (currentStatus === 'Active' && value === 'Inactive') {
        setShowConfirmationModal(true);
        // Don't update state immediately, wait for confirmation
        return;
      }
    }

    // State Sync
    if (name === 'state') {
      const selectedState = US_STATES.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        state: value,
        stateId: selectedState ? selectedState.id : null
      }));
      return;
    }

    // Username validation (alphanumeric only)
    if (name === 'username') {
      const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (!/^[a-zA-Z0-9]+$/.test(cleanedValue)) {
        setErrors(prev => ({ ...prev, username: 'Username should only contain alphanumeric characters' }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      }
      return;
    }

    // Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = value.replace(/\s/g, '');
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      
      setFormData(prev => ({ ...prev, email: cleanedValue }));
      
      if (!cleanedValue) {
        setErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else if (cleanedValue && !emailRegex.test(cleanedValue)) {
        setErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
      }
      return; // Handled update
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };
    if (fieldLabels[name] && (!value || !String(value).trim())) {
      newErrors[name] = `${fieldLabels[name]} is required`;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'password' && name !== 'email') {
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.username.trim())) {
      newErrors.username = 'Username should only contain alphanumeric characters';
    }

    // Password Validation
    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!isEditMode && !formData.confirmPassword?.trim()) newErrors.confirmPassword = 'Confirm Password is required';
    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    
    // City alpha validation
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    } else if (!/^[a-zA-Z\s]*$/.test(formData.city)) {
      newErrors.city = 'City can only contain alphabets';
    }

    if (!formData.state?.trim()) newErrors.state = 'State is required';
    
    // Zip Code
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.length < 2) {
      newErrors.zipCode = 'Zip code must be at least 2 characters';
    }

    // Email validation (trim extra spaces)
    const emailValue = (formData.email || '').trim();
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailValue) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(emailValue)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone validation (fully filled)
    const numericHome = (formData.homePhone || '').replace(/\D/g, '');
    if (!numericHome) {
      newErrors.homePhone = 'Home Phone is required';
    } else if (numericHome.length < 10) {
      newErrors.homePhone = 'Home Phone must be exactly 10 digits';
    }

    if (!formData.twoWayAuth?.trim()) newErrors.twoWayAuth = 'Two Way Authentication is required';
    if (!formData.timeZone?.trim()) newErrors.timeZone = 'Time Zone is required';
    if (!formData.status?.trim()) newErrors.status = 'Status is required';

    // Password confirmation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      try {
        await saveCaregiver();
      } catch (error) {
        const message = typeof error === 'string'
          ? error
          : (error?.response?.data?.message || error?.message || 'Failed to update caregiver');

        if (String(message).toLowerCase().includes('different from current password')) {
          setErrors(prev => ({ ...prev, password: message }));
        } else {
          toast.error(message);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      setShowValidationModal(true);
    }
  };

  const saveCaregiver = async () => {
    // Transform form data to match backend API
    const caregiverData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      middle_name: formData.middleInitial,
      email: (formData.email || '').trim(),
      phone: formData.homePhone,
      cell_phone: formData.cellPhone,
      fax: formData.fax,
      address_line_1: formData.addressLine1,
      address_line_2: formData.addressLine2,
      city: formData.city,
      state_id: formData.stateId || 1,
      zip_code: formData.zipCode,
      username: formData.username,
      password: formData.password,
      status: isEditMode ? (formData.status || 'Active') : 'Active',
      is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
      time_zone: formData.timeZone,
      title_id: formData.title ? parseInt(formData.title) : null
    };

    if (isEditMode) {
      if (!caregiverData.password) delete caregiverData.password;
      await dispatch(updateCaregiver({ practiceId, caregiverId: editId, caregiverData })).unwrap();
      toast.success('Caregiver updated successfully');
    } else {
      const tempId = `temp-caregiver-${Date.now()}`;
      const optimisticCaregiver = {
        id: tempId,
        name: [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim() || 'N/A',
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: (formData.email || '').trim(),
        phone: formData.homePhone || 'N/A',
        home_phone: formData.homePhone || 'N/A',
        cell_phone: formData.cellPhone || 'N/A',
        fax: formData.fax || 'N/A',
        status: formData.status || 'Active',
        is_two_way_auth: caregiverData.is_two_way_auth,
        two_way_authentication_enabled: caregiverData.is_two_way_auth === 1,
        address_line_1: formData.addressLine1 || 'N/A',
        address: {
          address_line_1: formData.addressLine1 || 'N/A'
        }
      };

      dispatch(addCaregiverOptimistic({ practiceId, caregiver: optimisticCaregiver }));
      await dispatch(createCaregiver({ practiceId, caregiverData, tempId })).unwrap();
      toast.success('Caregiver added successfully');
    }

    navigate(`/practices/${practiceId}?tab=caregiver`);
  };

  const startInactivationFlow = async () => {
    setShowConfirmationModal(false);
    setIsSubmitting(true);
    try {
      // 1. Fetch patients for this caregiver
      const patientsResponse = await api.get(`/practices/${practiceId}/patients`, { 
        params: { caregiverId: editId, limit: 100 } 
      });
      const patients = patientsResponse.data?.data?.patients || [];
      setAssignedPatients(patients);

      // 2. Fetch alternative caregivers
      const caregiversResponse = await api.get(`/practices/${practiceId}/caregivers`, { params: { limit: 1000 } });
      const rawCaregivers =
        caregiversResponse.data?.data?.caregivers ||
        caregiversResponse.data?.caregivers ||
        caregiversResponse.data?.data ||
        caregiversResponse.data ||
        [];
      const caregivers = (Array.isArray(rawCaregivers) ? rawCaregivers : []).filter((c) => {
        const status = c?.status;
        const isActive =
          status === 1 ||
          status === true ||
          (typeof status === 'string' && status.toLowerCase() === 'active');
        return String(c?.id) !== String(editId) && isActive;
      });
      setAlternativeCaregivers(caregivers);

      if (patients.length > 0) {
        const initialReassignments = {};
        patients.forEach((p) => {
          initialReassignments[p.id] = '';
        });
        setReassignments(initialReassignments);
        setShowReassignmentModal(true);
      } else {
        // No patients, proceed to set inactive and save
        setFormData(prev => ({ ...prev, status: 'Inactive' }));
        // We will save on the next user click or continue? 
        // User just selected "Inactive" and confirmed. We should probably update the status and let them click "Save".
        setFormData(prev => ({ ...prev, status: 'Inactive' }));
      }
    } catch (err) {
      console.error('Inactivation flow failed:', err);
      toast.error('Failed to initiate reassignment workflow');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchReassign = async () => {
    setIsReassigning(true);
    try {
      const missingAssignments = assignedPatients.some(patient => !reassignments[patient.id]);
      if (missingAssignments) {
        toast.warning('Please select a new caregiver for all patients');
        setIsReassigning(false);
        return;
      }

      const reassignArray = Object.entries(reassignments).map(([pid, cid]) => ({
        patientId: pid,
        caregiverId: cid
      }));

      await api.post(`/practices/${practiceId}/patients/batch-reassign`, { reassignments: reassignArray });
      
      setFormData(prev => ({ ...prev, status: 'Inactive' }));
      setShowReassignmentModal(false);
      toast.success('Patients reassigned. Status updated to Inactive.');
    } catch (err) {
      console.error('Batch reassignment failed:', err);
      toast.error('Failed to reassign patients');
    } finally {
      setIsReassigning(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCancel = () => {
    navigate(`/practices/${practiceId}?tab=caregiver`);
  };

  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  if (isEditMode && isLoading) {
    return (
      <div className="add-caregiver-main-container">
        <div className="add-caregiver-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={2} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <>
      <AddFormModel
        title={isEditMode ? 'Edit Practice Caregiver' : 'Add Practice Caregiver'}
        currentPractice={currentPractice}
        getLogoUrl={getLogoUrl}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        rows={buildPersonFormRows({
          formData,
          errors,
          isEditMode,
          handleInputChange,
          showPassword,
          showConfirmPassword,
          togglePasswordVisibility,
          toggleConfirmPasswordVisibility,
          phoneFieldName: 'homePhone',
          phoneLabel: 'Home Phone',
          phoneRequired: true,
        })}
        footerActions={
          <>
            {isEditMode ? (
              <button
                type="button"
                className="btn-submit"
                onClick={() => navigate(`/event-logs/Caregiver/${editId}?practiceId=${practiceId || ''}`)}
                title="View Change History"
                style={{ width: 'auto', paddingLeft: '15px', paddingRight: '15px', backgroundColor: '#19242E', marginRight: 'auto', height: '40px' }}
              >
                <FontAwesomeIcon icon={faList} className="me-2" /> Events Log
              </button>
            ) : (
              <div style={{ marginRight: 'auto' }}></div>
            )}
            <FormActionButtons
              onCancel={handleCancel}
              disabled={
                isSubmitting ||
                isLoading ||
                isUpdating ||
                isCreating ||
                isCheckingPasswordReuse ||
                (isEditMode && !isChanged) ||
                Boolean(errors.password) ||
                Boolean(errors.confirmPassword) ||
                hasPasswordReuseError
              }
              isSubmitting={isSubmitting || isLoading || isUpdating || isCreating}
              isEditMode={isEditMode}
            />
          </>
        }
      />

      {/* Validation Error Modal */}
      <Modal 
        show={showValidationModal} 
        onHide={() => setShowValidationModal(false)} 
        centered 
        className="add-caregiver-validation-modal"
      >
        <Modal.Body className="add-caregiver-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-caregiver-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="add-caregiver-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
          </div>
          <h4 className="add-caregiver-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button 
            type="button"
            className="add-caregiver-modal-btn" 
            onClick={() => setShowValidationModal(false)}
            style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>

      {/* Inactivation Confirmation Modal */}
      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        centered
        dialogClassName="add-caregiver-confirm-dialog"
        contentClassName="add-caregiver-confirm-content"
      >
        <Modal.Body className="add-caregiver-confirm-body text-center">
          <button
            type="button"
            className="add-caregiver-confirm-close"
            aria-label="Close"
            onClick={() => setShowConfirmationModal(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className="add-caregiver-confirm-icon-wrapper mx-auto">
            <FontAwesomeIcon icon={faInfoCircle} />
          </div>
          <h4 className="add-caregiver-confirm-title">Are you sure?</h4>
          <p className="add-caregiver-confirm-text">
            Remove access to this caregiver and reassign assigned patients.
          </p>

          <div className="add-caregiver-confirm-actions">
            <button
              type="button"
              className="add-caregiver-confirm-btn add-caregiver-confirm-btn--ghost"
              onClick={() => setShowConfirmationModal(false)}
            >
              No
            </button>
            <button
              type="button"
              className="add-caregiver-confirm-btn add-caregiver-confirm-btn--primary"
              onClick={startInactivationFlow}
            >
              Yes
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <ReassignmentModal
        show={showReassignmentModal}
        onClose={() => setShowReassignmentModal(false)}
        title="Reassign Patients"
        description="Select a new caregiver for each patient before inactivation."
        patients={assignedPatients}
        alternatives={alternativeCaregivers}
        reassignments={reassignments}
        showMeta={false}
        showIndex={true}
        hideCurrentColumn={true}
        showOptionPlaceholder={true}
        onReassignmentChange={(patientId, caregiverId) =>
          setReassignments((prev) => ({ ...prev, [patientId]: caregiverId }))
        }
        onSave={handleBatchReassign}
        isSaving={isReassigning}
        saveLabel="Save & Inactivate"
        optionPlaceholder="Select caregiver..."
        reassignToLabel="Reassign to caregiver"
        getCurrentAssignee={(patient) =>
          patient.caregiver_name ||
          patient.provider_name ||
          patient.assigned_to_name ||
          patient.current_caregiver_name ||
          ((patient.caregiver_first_name || patient.caregiver_last_name)
            ? `${patient.caregiver_first_name || ''} ${patient.caregiver_last_name || ''}`.trim()
            : '') ||
          ((patient.provider_first_name || patient.provider_last_name)
            ? `${patient.provider_first_name || ''} ${patient.provider_last_name || ''}`.trim()
            : '') ||
          'N/A'
        }
      />
    </>
  );
};

export default AddCaregiver;